var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { utils } from "@tradetrust-tt/tradetrust";
import { DocumentStoreFactory } from "@tradetrust-tt/document-store";
import { errors } from "ethers";
import { OpenAttestationEthereumDocumentStoreStatusCode, OpenAttestationDidSignedDocumentStatusCode, } from "../../types/error";
import { CodedError } from "../../common/error";
import { OcspResponderRevocationReason } from "./revocation.types";
import { ValidOcspResponse, ValidOcspResponseRevoked } from "./didSigned/didSignedDocumentStatus.type";
export const getIntermediateHashes = (targetHash, proofs = []) => {
    const hashes = [`0x${targetHash}`];
    proofs.reduce((prev, curr) => {
        const next = utils.combineHashString(prev, curr);
        hashes.push(`0x${next}`);
        return next;
    }, targetHash);
    return hashes;
};
/**
 * Try to decode the error to see if we can deterministically tell if the document has NOT been issued or revoked.
 *
 * In case where we cannot tell, we throw an error
 * */
export const decodeError = (error) => {
    var _a, _b, _c, _d;
    const reason = error.reason && Array.isArray(error.reason) ? error.reason[0] : (_a = error.reason) !== null && _a !== void 0 ? _a : "";
    switch (true) {
        case !error.reason &&
            (((_b = error.method) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === "isRevoked(bytes32)".toLowerCase() ||
                ((_c = error.method) === null || _c === void 0 ? void 0 : _c.toLowerCase()) === "isIssued(bytes32)".toLowerCase()) &&
            error.code === errors.CALL_EXCEPTION:
            return "Contract is not found";
        case reason.toLowerCase() === "ENS name not configured".toLowerCase() &&
            error.code === errors.UNSUPPORTED_OPERATION:
            return "ENS name is not configured";
        case reason.toLowerCase() === "bad address checksum".toLowerCase() && error.code === errors.INVALID_ARGUMENT:
            return "Bad document store address checksum";
        case ((_d = error.message) === null || _d === void 0 ? void 0 : _d.toLowerCase()) === "name not found".toLowerCase():
            return "ENS name is not found";
        case reason.toLowerCase() === "invalid address".toLowerCase() && error.code === errors.INVALID_ARGUMENT:
            return "Invalid document store address";
        case error.code === errors.INVALID_ARGUMENT:
            return "Invalid call arguments";
        case error.code === errors.SERVER_ERROR:
            throw new CodedError("Unable to connect to the Ethereum network, please try again later", OpenAttestationEthereumDocumentStoreStatusCode.SERVER_ERROR, OpenAttestationEthereumDocumentStoreStatusCode[OpenAttestationEthereumDocumentStoreStatusCode.SERVER_ERROR]);
        default:
            throw error;
    }
};
/**
 * Given a list of hashes, check against one smart contract if any of the hash has been revoked
 * */
export const isAnyHashRevoked = (smartContract, intermediateHashes) => __awaiter(void 0, void 0, void 0, function* () {
    const revokedStatusDeferred = intermediateHashes.map((hash) => smartContract.isRevoked(hash).then((status) => (status ? hash : undefined)));
    const revokedStatuses = yield Promise.all(revokedStatusDeferred);
    return revokedStatuses.find((hash) => hash);
});
export const isRevokedOnDocumentStore = ({ documentStore, merkleRoot, provider, targetHash, proofs, }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const documentStoreContract = yield DocumentStoreFactory.connect(documentStore, provider);
        const intermediateHashes = getIntermediateHashes(targetHash, proofs);
        const revokedHash = yield isAnyHashRevoked(documentStoreContract, intermediateHashes);
        return revokedHash
            ? {
                revoked: true,
                address: documentStore,
                reason: {
                    message: `Document ${merkleRoot} has been revoked under contract ${documentStore}`,
                    code: OpenAttestationEthereumDocumentStoreStatusCode.DOCUMENT_REVOKED,
                    codeString: OpenAttestationEthereumDocumentStoreStatusCode[OpenAttestationEthereumDocumentStoreStatusCode.DOCUMENT_REVOKED],
                },
            }
            : {
                revoked: false,
                address: documentStore,
            };
    }
    catch (error) {
        // If error can be decoded and it's because of document is not revoked, we return false
        // Else allow error to continue to bubble up
        return {
            revoked: true,
            address: documentStore,
            reason: {
                message: decodeError(error),
                code: OpenAttestationEthereumDocumentStoreStatusCode.DOCUMENT_REVOKED,
                codeString: OpenAttestationEthereumDocumentStoreStatusCode[OpenAttestationEthereumDocumentStoreStatusCode.DOCUMENT_REVOKED],
            },
        };
    }
});
export const isRevokedByOcspResponder = ({ merkleRoot, targetHash, proofs, location, }) => __awaiter(void 0, void 0, void 0, function* () {
    const intermediateHashes = getIntermediateHashes(targetHash, proofs);
    for (const hash of intermediateHashes) {
        const res = yield fetch(`${location}/${hash}`).catch((e) => {
            throw new CodedError(`Invalid or unexpected response from OCSP Responder - ${e}`, OpenAttestationDidSignedDocumentStatusCode.OCSP_RESPONSE_INVALID, "OCSP_RESPONSE_INVALID");
        });
        const data = yield res.json();
        if (ValidOcspResponse.guard(data)) {
            continue;
        }
        else if (ValidOcspResponseRevoked.guard(data)) {
            const { reasonCode } = data;
            return {
                revoked: true,
                address: location,
                reason: {
                    message: `Document ${merkleRoot} has been revoked under OCSP Responder: ${location}`,
                    code: reasonCode,
                    codeString: OcspResponderRevocationReason[reasonCode],
                },
            };
        }
        else {
            throw new CodedError(`Invalid or unexpected response from OCSP Responder`, OpenAttestationDidSignedDocumentStatusCode.OCSP_RESPONSE_INVALID, "OCSP_RESPONSE_INVALID");
        }
    }
    return {
        revoked: false,
        address: location,
    };
});
